import { v4 as uuidv4 } from 'uuid'
import { IEntry } from '../models/Entry'
import { IUser } from '../models/User'

export interface IAppState {
  user: IUser
  entries: IEntry[]
  likedEntryIds: string[]
  dislikedEntryIds: string[]
}

export const appInitialState: IAppState = {
  user: {
    id: 1,
    username: 'John Doe',
    avatar: 'https://api.dicebear.com/7.x/bottts/svg',
  },
  entries: [],
  likedEntryIds: [],
  dislikedEntryIds: [],
}

export enum AppActionType {
  InitState = 'INIT_STATE',
  AddEntry = 'ADD_ENTRY',
  RemoveEntry = 'REMOVE_ENTRY',
  UpdateEntry = 'UPDATE_ENTRY',
  LikeEntry = 'LIKE_ENTRY',
  DislikeEntry = 'DISLIKE_ENTRY',
}

export interface IAppAction {
  type: AppActionType
  data: unknown
}

export function appReducer(state: IAppState, action: IAppAction): IAppState {
  switch (action.type) {
    case AppActionType.InitState:
      return action.data as IAppState
    case AppActionType.AddEntry:
      return {
        ...state,
        entries: [
          {
            id: uuidv4(),
            user: state.user,
            createdAtUnixMS: Date.now(),
            updatedAtUnixMS: null,
            content: action.data as string,
            likeCount: 0,
            dislikeCount: 0,
          },
          ...state.entries,
        ],
      }
    case AppActionType.RemoveEntry:
      return {
        ...state,
        entries: state.entries.filter((entry) => entry.id !== action.data),
      }
    case AppActionType.UpdateEntry: {
      const data = action.data as IEntry

      return {
        ...state,
        entries: state.entries.map((entry) => {
          if (entry.id !== data.id) return entry
          return {
            ...entry,
            ...data,
            updatedAtUnixMS: Date.now(),
          }
        }),
      }
    }
    case AppActionType.LikeEntry: {
      const isAlreadyLiked = state.likedEntryIds.includes(action.data as string)
      if (isAlreadyLiked) {
        return {
          ...state,
          entries: state.entries.map((entry) => {
            if (entry.id !== action.data) return entry
            return {
              ...entry,
              likeCount: entry.likeCount - 1,
            }
          }),
          likedEntryIds: state.likedEntryIds.filter((id) => id !== action.data),
        }
      }

      const isDisliked = state.dislikedEntryIds.includes(action.data as string)
      return {
        ...state,
        entries: state.entries.map((entry) => {
          if (entry.id !== action.data) return entry
          return {
            ...entry,
            likeCount: entry.likeCount + 1,
            dislikeCount: isDisliked ? entry.dislikeCount - 1 : entry.dislikeCount,
          }
        }),
        likedEntryIds: [...state.likedEntryIds, action.data as string],
        dislikedEntryIds: state.dislikedEntryIds.filter((id) => id !== action.data),
      }
    }

    case AppActionType.DislikeEntry: {
      const isAlreadyDisliked = state.dislikedEntryIds.includes(action.data as string)
      if (isAlreadyDisliked) {
        return {
          ...state,
          entries: state.entries.map((entry) => {
            if (entry.id !== action.data) return entry
            return {
              ...entry,
              dislikeCount: entry.dislikeCount - 1,
            }
          }),
          dislikedEntryIds: state.dislikedEntryIds.filter((id) => id !== action.data),
        }
      }

      const isLiked = state.likedEntryIds.includes(action.data as string)

      return {
        ...state,
        entries: state.entries.map((entry) => {
          if (entry.id !== action.data) return entry
          return {
            ...entry,
            likeCount: isLiked ? entry.likeCount - 1 : entry.likeCount,
            dislikeCount: entry.dislikeCount + 1,
          }
        }),
        likedEntryIds: state.likedEntryIds.filter((id) => id !== action.data),
        dislikedEntryIds: [...state.dislikedEntryIds, action.data as string],
      }
    }

    default:
      throw new Error(`Invalid action type ${action.type} passed to appReducer`)
  }
}
